import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo } from "react";
import { PREFERENCE_TYPE, PREFERENCES_TYPE_MAPPER } from "@/constants";
import { ADDRESS_INITIAL_VALUES } from "@/constants/provider/patient/add-new-patient/add-new-patient-form";
import { PHONE_MASK } from "@/constants/referring-agency-constant";
import { useCreatePharmacyLabRadiology } from "@/queries/patient-chart/order/use-create-pharmacy-lab-radiology";
import { useUpdatePharmacyLabRadiology } from "@/queries/patient-chart/order/use-update-pharmacy-lab-radiology";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import MaskInput from "@/components/MaskInput";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import NumberInput from "@/components/NumberInput";
import AddressCommon from "./AddressCommon";
import {
  PREFERENCE_DRAWER_TITLE,
  PREFERENCE_SUBMIT_LABEL,
  PREFERENCE_VALIDATION_SCHEMA,
} from "./preferences-constant";
import { toPharmacyLabRadiology } from "./preferences-utils";
import { useSnackbar } from "@/hooks/useSnackbar";
import { getStoredRealm } from "@/utils/auth-storage";

export interface PreferenceFormValues {
  preferenceType: string;
  name: string;
  address: typeof ADDRESS_INITIAL_VALUES;
  contactNumber: string;
  faxNumber: string;
}

interface PatientLabPharmacyRadiologyFormDrawerProps {
  opened: boolean;
  handleCloseModal: () => void;
  /** Preselects and locks the preference type (e.g. "PHARMACY"). */
  preferenceType?: string;
  /** Called with the saved values and the server's record once the preference is saved. */
  onSaved?: (values: PreferenceFormValues, record?: unknown) => void;
  /** Updates this existing preference instead of creating a new one. */
  preferenceUuid?: string;
  /** Prefills the form, e.g. to edit an existing entry. */
  initialValues?: PreferenceFormValues;
  /** Replaces creating the preference; the drawer only collects the values. */
  onSubmit?: (values: PreferenceFormValues) => void;
  title?: string;
  submitLabel?: string;
}

const EMPTY_VALUES: PreferenceFormValues = {
  preferenceType: "",
  name: "",
  address: ADDRESS_INITIAL_VALUES,
  contactNumber: "",
  faxNumber: "",
};

const PatientLabPharmacyRadiologyFormDrawer = ({
  opened,
  handleCloseModal,
  preferenceType,
  onSaved,
  preferenceUuid,
  initialValues,
  onSubmit,
  title = PREFERENCE_DRAWER_TITLE,
  submitLabel = PREFERENCE_SUBMIT_LABEL,
}: PatientLabPharmacyRadiologyFormDrawerProps) => {
  const { mutateAsync: createPharmacyLabRadiology } =
    useCreatePharmacyLabRadiology();
  const { mutateAsync: updatePharmacyLabRadiology } =
    useUpdatePharmacyLabRadiology();

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const formInitialValues = useMemo(
    () =>
      initialValues ?? {
        ...EMPTY_VALUES,
        preferenceType: preferenceType ?? EMPTY_VALUES.preferenceType,
      },
    [initialValues, preferenceType]
  );

  const preferencesFormik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema: PREFERENCE_VALIDATION_SCHEMA,
    onSubmit: handleSave,
  });

  function handleSave(vals: PreferenceFormValues) {
    if (onSubmit) {
      onSubmit(vals);
      closeAndResetModal();
      return;
    }
    const pharmacyLabRadiology = toPharmacyLabRadiology(vals);
    const xTENANTID = getStoredRealm() ?? undefined;
    const apiCall = preferenceUuid
      ? updatePharmacyLabRadiology({
          preferenceUuid,
          pharmacyLabRadiology,
          xTENANTID,
        })
      : createPharmacyLabRadiology({
          parmacyLabRadiology: pharmacyLabRadiology,
          xTENANTID,
        });
    showToastAndResetForm(apiCall, vals);
  }

  async function showToastAndResetForm(
    apiCall: Promise<any>,
    saved: PreferenceFormValues
  ) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-orders-list"],
        });
        if (preferenceUuid)
          queryClient.invalidateQueries({ queryKey: ["patient"] });

        onSaved?.(saved, res?.data?.data);
        closeAndResetModal();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  function closeAndResetModal() {
    handleCloseModal();
    preferencesFormik.resetForm();
  }

  return (
    <SideSheetDrawer
      open={opened}
      title={title}
      w="800px"
      footerActions={
        <Button
          variant="contained"
          type="submit"
          onClick={preferencesFormik.submitForm}
        >
          {submitLabel}
        </Button>
      }
      onClose={closeAndResetModal}
    >
      <Flex flexDirection={"column"} gap={2} mt={2}>
        <Grid item xs={6}>
          <CustomSelect
            required
            label="Preferences Type"
            value={preferencesFormik.values.preferenceType}
            options={PREFERENCE_TYPE}
            readOnly={!!preferenceType}
            onChange={(val) =>
              preferencesFormik.setFieldValue("preferenceType", val)
            }
            placeholder="Select"
            error={
              preferencesFormik.touched?.preferenceType &&
              preferencesFormik?.errors?.preferenceType
            }
          />
        </Grid>
        <Input
          label={`${
            preferencesFormik.values.preferenceType
              ? PREFERENCES_TYPE_MAPPER[preferencesFormik.values.preferenceType]
              : "Preference Name"
          }`}
          required
          error={
            preferencesFormik.touched?.name && preferencesFormik?.errors?.name
          }
          textFieldProps={{
            placeholder: `${
              preferencesFormik.values.preferenceType
                ? `Enter ${
                    PREFERENCES_TYPE_MAPPER[
                      preferencesFormik.values.preferenceType
                    ]
                  }`
                : "Enter Preference Name"
            }`,
            onChange: preferencesFormik.handleChange,
            onBlur: preferencesFormik.handleBlur,
            name: "name",
            value: preferencesFormik.values.name,
          }}
        />
        <AddressCommon formik={preferencesFormik} formikKey={`address`} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <NumberInput
              label="Fax Number"
              textFieldProps={{
                placeholder: "Enter Fax Number",
                name: "faxNumber",
                value: preferencesFormik.values.faxNumber,
                // Formik's handleChange stores number inputs as numbers; the pharmacy card needs a string.
                onChange: (e) =>
                  preferencesFormik.setFieldValue("faxNumber", e.target.value),
                onBlur: preferencesFormik.handleBlur,
              }}
              error={
                preferencesFormik.touched.faxNumber &&
                preferencesFormik.errors.faxNumber
              }
            />
          </Grid>

          <Grid item xs={6}>
            <MaskInput
              mask={PHONE_MASK}
              label="Contact Number"
              name="contact"
              error={preferencesFormik.errors?.contactNumber}
              placeholder="Enter Contact Number"
              value={preferencesFormik.values.contactNumber}
              onChange={(e) =>
                preferencesFormik.setFieldValue("contactNumber", e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Flex>
    </SideSheetDrawer>
  );
};

export default PatientLabPharmacyRadiologyFormDrawer;
